import { watch } from "vue";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/stores/auth.store";
import {
  clearStaleBearerForAnchorPlan,
} from "@/services/auth.service";
import {
  anchorPlanService,
  type AnchorPlanUser,
} from "@/services/anchorPlan.service";

/** Користувач основного Semantica → формат Anchor Plan (резерв, якщо /auth/me тимчасово без user). */
export function mapMainUserToAnchorPlanUser(main: {
  id: number;
  email: string;
  name?: string | null;
  role?: string;
}): AnchorPlanUser {
  return {
    id: main.id,
    email: main.email,
    name: main.name ?? null,
    picture: null,
    is_admin: main.role === "admin",
  };
}

/**
 * Єдина сесія для SPA: cookie Sanctum + /api/user.
 * Викликати перед Anchor Plan і після повернення з /app.
 */
export async function ensureSharedSession(): Promise<void> {
  const authStore = useAuthStore();
  await authStore.checkAuth();
  if (authStore.isAuthenticated) {
    await clearStaleBearerForAnchorPlan();
  }
}

/** Anchor Plan user: спочатку API, інакше — з Pinia після успішного /api/user. */
export async function resolveAnchorPlanUser(): Promise<AnchorPlanUser | null> {
  await ensureSharedSession();
  const authStore = useAuthStore();
  if (!authStore.isAuthenticated || !authStore.user) {
    return null;
  }
  try {
    const { data } = await anchorPlanService.me();
    if (data.user) {
      return data.user;
    }
  } catch {
    /* cookie-сесія є, me тимчасово недоступний — показуємо з основного додатку */
  }
  return mapMainUserToAnchorPlanUser(authStore.user);
}

export function useSharedAuth() {
  const authStore = useAuthStore();
  const { user, isAuthenticated, isLoading } = storeToRefs(authStore);

  return {
    user,
    isAuthenticated,
    isLoading,
    ensureSharedSession,
    resolveAnchorPlanUser,
    mapMainUserToAnchorPlanUser,
  };
}

/** Оновити Anchor Plan UI при зміні сесії основного додатку. */
export function watchSharedAuth(onChange: () => void): () => void {
  const authStore = useAuthStore();
  return watch(
    () => authStore.isAuthenticated,
    () => {
      onChange();
    },
  );
}
